/**
 * Aircraft detail enricher: real ADS-B telemetry (origin country, altitude,
 * speed, heading, on-ground state). The "military" flag from the feed is a
 * heuristic and is labelled as such. Altitude/speed are shown in both aviation
 * (ft/kt) and SI units. No fabricated fields.
 */

import type { Track } from '@vtak/shared';
import type { MarkerData } from '@/map/markers/marker-state';
import { type EntityDetail, formatCoords, textRow } from './entity-detail';

/** Layer id for live aircraft tracks. */
export const AIRCRAFT_LAYER = 'air:aircraft';

/** Feet per metre. */
const FEET_PER_M = 3.28084;

/** Knots per metre-per-second. */
const KNOTS_PER_MS = 1.94384;

/** Build the aircraft detail view from real ADS-B props. */
export function enrichAircraft(data: MarkerData, track: Track | undefined): EntityDetail {
  const props = track?.props ?? {};
  const altitudeM = numberProp(props, 'altitude');
  const velocityMs = numberProp(props, 'velocity');
  const heading = numberProp(props, 'heading');
  const military = props['military'] === true;
  const onGround = props['onGround'] === true;

  return {
    sections: [
      {
        title: 'Flight',
        rows: [
          textRow('Callsign', data.callsign),
          textRow('Origin country', props['country']),
          { label: 'Classification', value: military ? 'Military (heuristic)' : 'Civil' },
          { label: 'Status', value: onGround ? 'On ground' : 'Airborne' },
        ],
      },
      {
        title: 'Telemetry',
        rows: [
          { label: 'Altitude', value: dualUnit(altitudeM * FEET_PER_M, 'ft', altitudeM, 'm') },
          { label: 'Speed', value: dualUnit(velocityMs * KNOTS_PER_MS, 'kt', velocityMs, 'm/s') },
          { label: 'Heading', value: `${Math.round(heading)}°` },
          { label: 'Position', value: formatCoords(data.position) },
        ],
      },
    ],
    followUps: military ? ['Military classification is a feed heuristic, not a verified identity.'] : undefined,
  };
}

/** Read a numeric track prop, defaulting to 0. */
function numberProp(props: Record<string, unknown>, key: string): number {
  const value = props[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** "12,345 ft (3,762 m)" style dual-unit value. */
function dualUnit(primary: number, primaryUnit: string, secondary: number, secondaryUnit: string): string {
  return `${Math.round(primary).toLocaleString()} ${primaryUnit} (${Math.round(secondary).toLocaleString()} ${secondaryUnit})`;
}
